import { useState } from "react";

/**
 * Public "up funnel" for unsold_visit recipients — someone who visited the
 * dealership and did NOT buy. Rendered by FunnelPage at /r/:token when the
 * server tags its payload `kind: "unsold"`.
 *
 * Three questions, server-driven copy (survey_flow.ts's
 * DEFAULT_UNSOLD_REASON_PROMPT / DEFAULT_UNSOLD_REASON_OPTIONS /
 * DEFAULT_UNSOLD_FOLLOWUP_PROMPT are only the fallback if a request ever
 * arrives with the field unset):
 *
 *   Q1  survey.reasonPrompt   — single-select from survey.reasonOptions,
 *                                shuffled per respondent, "Other" pinned last
 *                                with a free-text box behind it
 *   Q2  "How were you treated?" — Yes / No, stored through the same
 *                                satisfaction-score mechanism as the review
 *                                funnel (scoreForGreatJob / didGreatJob) so
 *                                downstream reporting keeps working
 *   Q3  survey.followupPrompt — optional free text
 *   Exit  thanks
 *
 * 🚨 COMPLIANCE — THIS FUNNEL HAS NO REVIEW ASK ANYWHERE IN IT.
 * Asking someone who just walked out without buying to post a public Google
 * review invites exactly the bad review this product exists to prevent. This
 * file must never render a review CTA, a `/go` link, or any reference to a
 * review URL — see showsReviewCta() in services/survey_flow.ts, which makes
 * that a fact of the campaign TYPE, not a default this page could flip. The
 * server never even sends a reviewUrl for this kind, but the rule here is
 * belt-and-suspenders: no such code path exists in this component at all.
 *
 * The incentive banner/exit line follow the exact same compliance shape as
 * FunnelPage.tsx: earned by COMPLETING THE SURVEY, identical regardless of
 * the Q2 answer, never conditioned on any response.
 */

/** Mirrors DEFAULT_UNSOLD_REASON_PROMPT — used only if the request itself failed. */
const FALLBACK_REASON_PROMPT = "What kept you from buying that day?";

/** Mirrors DEFAULT_UNSOLD_FOLLOWUP_PROMPT — used only if the request itself failed. */
const FALLBACK_FOLLOWUP_PROMPT = "Anything we could have done differently?";

/** Fallback list for a campaign with no configured options (server sends its own). */
const FALLBACK_REASON_OPTIONS = [
  "The price",
  "What you offered for my trade",
  "Financing or payment terms",
  "The vehicle wasn't right",
  "I'm still deciding",
  "I bought somewhere else",
  "Other",
];

interface UnsoldSurveyConfig {
  reasonPrompt: string;
  reasonOptions: string[];
  followupPrompt: string;
  incentiveText: string | null;
  incentivePromptText: string | null;
}

export interface UnsoldState {
  kind: "unsold";
  state: "open" | "responded" | "expired";
  firstName?: string | null;
  dealerName?: string;
  visitDate?: string | null;
  vehicle?: string | null;
  survey?: UnsoldSurveyConfig;
  showReviewCta?: false;
}

type Step = "reason" | "treatment" | "followup" | "thanks" | "expired";

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl bg-white p-6 shadow-sm">{children}</div>;
}

export function UnsoldFunnel({
  token,
  initial,
}: {
  token: string | undefined;
  initial: UnsoldState;
}) {
  const [step, setStep] = useState<Step>(
    initial.state === "expired" ? "expired" : initial.state === "responded" ? "thanks" : "reason"
  );
  const [reason, setReason] = useState<string | null>(null);
  const [otherText, setOtherText] = useState("");
  const [treatedWell, setTreatedWell] = useState<boolean | null>(null);
  const [followup, setFollowup] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const dealer = initial.dealerName ?? "our dealership";
  const hello = initial.firstName ? `Hi ${initial.firstName}!` : "Hi there!";
  const options = initial.survey?.reasonOptions?.length
    ? initial.survey.reasonOptions
    : FALLBACK_REASON_OPTIONS;
  const reasonPrompt = initial.survey?.reasonPrompt?.trim() || FALLBACK_REASON_PROMPT;
  const followupPrompt = initial.survey?.followupPrompt?.trim() || FALLBACK_FOLLOWUP_PROMPT;
  const incentiveText = initial.survey?.incentiveText;
  // Up-front banner, identical on every live step regardless of any answer —
  // same compliance shape as FunnelPage.tsx's incentivePrompt.
  const incentivePrompt = initial.survey?.incentivePromptText?.trim() || null;
  const onSurveyStep = step === "reason" || step === "treatment" || step === "followup";

  /** Q1 → Q2. Free text behind "Other" travels with it; nothing is posted yet. */
  function submitReason() {
    if (!reason) return;
    setStep("treatment");
  }

  /** Q2 → Q3. Still nothing posted — one write, fired once the customer is done. */
  function submitTreatment(wasGood: boolean) {
    setTreatedWell(wasGood);
    setStep("followup");
  }

  /**
   * The single write for this funnel: Q1 + Q2 + optional Q3 together, fired
   * once at the end (Submit or Skip on the last step). One POST keeps this in
   * the same "first answer wins" idempotency shape as the rest of the funnel
   * without inventing a second endpoint this task doesn't own (Task 8 builds
   * /api/public/funnel/:token/unsold).
   */
  async function finish() {
    if (submitting || treatedWell === null) return;
    setSubmitting(true);
    const reasonAnswer =
      reason === "Other" && otherText.trim() ? otherText.trim() : (reason ?? undefined);
    try {
      await post(`/api/public/funnel/${token}/unsold`, {
        unsoldReason: reasonAnswer,
        // Stored through the existing satisfaction-score mechanism (Yes=5/No=1)
        // so downstream reporting/routing that keys off satisfaction_score
        // keeps working for this campaign type too.
        didGreatJob: treatedWell,
        ...(followup.trim() ? { followup: followup.trim() } : {}),
      });
    } catch {
      // Non-blocking: still show the thanks step so the customer isn't stuck.
    }
    setStep("thanks");
    setSubmitting(false);
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900">{dealer}</h1>
        </div>

        {/* Deliberately outside every step's Card and above all of them, taking
            no answer as input — identical for every respondent regardless of
            what they end up saying. See the compliance note at the top of this
            file. */}
        {onSurveyStep && incentivePrompt && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
            <p className="text-sm font-medium text-amber-900">{incentivePrompt}</p>
          </div>
        )}

        {step === "expired" && (
          <Card>
            <p className="text-center text-slate-700">This link is no longer active.</p>
            <p className="mt-2 text-center text-sm text-slate-500">
              We'd still love to hear from you — give us a call or stop by anytime.
            </p>
          </Card>
        )}

        {/* Q1 — single-select, shuffled per respondent, "Other" pinned last. */}
        {step === "reason" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">{hello}</p>
            <p className="mt-1 text-slate-600">{reasonPrompt}</p>
            <div className="mt-4 flex flex-col gap-2">
              {options.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setReason(opt)}
                  className={`rounded-lg border px-4 py-3 text-left text-sm transition ${
                    reason === opt
                      ? "border-blue-600 bg-blue-50 font-medium text-blue-900"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {opt}
                </button>
              ))}
              {reason === "Other" && (
                <input
                  type="text"
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder="Tell us more (optional)"
                  maxLength={80}
                  className="rounded-lg border border-slate-200 px-4 py-3 text-sm"
                />
              )}
            </div>
            <button
              type="button"
              disabled={!reason}
              onClick={submitReason}
              className="mt-5 w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white disabled:opacity-40"
            >
              Next
            </button>
          </Card>
        )}

        {/* Q2 — stored through the satisfaction-score mechanism. */}
        {step === "treatment" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">How were you treated?</p>
            <div className="mt-5 flex flex-col gap-3">
              <button
                type="button"
                onClick={() => submitTreatment(true)}
                className="rounded-lg bg-emerald-600 px-4 py-4 font-medium text-white disabled:opacity-50"
              >
                Yes, great
              </button>
              <button
                type="button"
                onClick={() => submitTreatment(false)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-4 font-medium text-slate-700 disabled:opacity-50"
              >
                No
              </button>
            </div>
          </Card>
        )}

        {/* Q3 — optional free text. */}
        {step === "followup" && (
          <Card>
            <p className="text-lg font-medium text-slate-900">{followupPrompt}</p>
            <textarea
              value={followup}
              onChange={(e) => setFollowup(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Optional"
              className="mt-3 w-full rounded-lg border border-slate-200 p-3 text-sm"
            />
            <button
              type="button"
              disabled={submitting}
              onClick={() => void finish()}
              className="mt-3 w-full rounded-lg bg-blue-600 px-4 py-3 font-medium text-white disabled:opacity-40"
            >
              {submitting ? "Saving…" : followup.trim() ? "Submit" : "Skip"}
            </button>
          </Card>
        )}

        {step === "thanks" && (
          <Card>
            <p className="text-center text-lg font-medium text-slate-900">
              Thank you for the feedback.
            </p>
            {treatedWell === false && (
              <p className="mt-2 text-center text-sm text-slate-600">
                We take this seriously and want to make it right.
              </p>
            )}
            {/* Earned by finishing the survey — identical regardless of the Q2
                answer. See the compliance note at the top of this file. */}
            {incentiveText && (
              <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-center text-sm text-amber-900">
                {incentiveText}
              </p>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
